import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { formatFoldersWithSubFolders, getAvailableFolders, buildBreadcrumbPath } from "../utils";
import { Breadcrumb } from "../FolderBreadcrumbs/types";

import { Folder } from "@/types/folders";
import { useFolders, useMoveFolderEntity } from "@/queries";
import { useUser } from "@/context/user-context";
import { useAuth } from "@/context/auth-context";
import { displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";
import { Entity } from "@/queries/folder-entities";
import { useUnifiedRegistry } from "@/components/UnifiedRegistry/unified-registry-context";

export const useMoveToFolderModal = (
  entities: Entity[],
  setOpen: (open: boolean) => void,
  open: boolean,
) => {
  const { folderId: currentFolderId } = useParams();
  const [searchTerm, setSearchTerm] = useState("");

  const getInitialFolderId = useCallback((): number | null => {
    if (entities.length >= 1) {
      return entities[0]?.folder_id ?? null;
    }

    if (currentFolderId) {
      return Number(currentFolderId);
    }

    return null;
  }, [entities, currentFolderId]);

  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(getInitialFolderId());

  useEffect(() => {
    setSelectedFolderId(getInitialFolderId());
  }, [entities, currentFolderId, getInitialFolderId]);

  useEffect(() => {
    if (!open) {
      setSelectedFolderId(currentFolderId ? Number(currentFolderId) : null);
    }
  }, [open, setSelectedFolderId, currentFolderId]);

  const authContext = useAuth();
  const userContext = useUser();
  const queryClient = useQueryClient();
  const registry = useUnifiedRegistry();
  const workspaceId = userContext.activeWorkspaceId!;
  const { userToken } = authContext!;
  const moveFolderEntity = useMoveFolderEntity(userToken!);
  const foldersQuery = useFolders(userToken!, workspaceId!);

  const allFolders = useMemo(
    (): Folder[] => formatFoldersWithSubFolders(foldersQuery.data?.folders ?? []),
    [foldersQuery.data?.folders],
  );

  const folderList = useMemo(
    (): Folder[] => getAvailableFolders(entities, allFolders),
    [entities, allFolders],
  );

  const currentFolder = useMemo(() => {
    if (!selectedFolderId || allFolders.length === 0) {
      return null;
    }
    return allFolders.find(f => f.id === selectedFolderId) ?? null;
  }, [selectedFolderId, allFolders]);

  const breadcrumbs = useMemo(
    (): Breadcrumb[] => buildBreadcrumbPath(selectedFolderId, allFolders),
    [selectedFolderId, allFolders],
  );

  const handleSelectFolder = (folderId: number) => {
    setSelectedFolderId(folderId);

    if (searchTerm) {
      setSearchTerm("");
    }
  };

  const handleGoBack = () => {
    if (currentFolder?.parent_id !== undefined) {
      setSelectedFolderId(currentFolder.parent_id);
    }
  };

  const handleMove = async () => {
    if (entities.length === 0) {
      return;
    }

    try {
      await moveFolderEntity.mutateAsync({
        entities,
        folder_id: selectedFolderId,
      });

      const destinationName = currentFolder?.name ?? "Home";

      const itemText =
        entities.length === 1 ? entities[0].name || "Item" : `${entities.length} items`;

      displayToast(`${itemText} moved to ${destinationName}`, ToastType.success);
      const originFolder = allFolders.find(f => f.id === entities[0].folder_id)?.id;

      registry.clearSelection();

      queryClient.invalidateQueries({
        queryKey: ["folder-entities", String(workspaceId), originFolder ? originFolder : undefined],
      });

      queryClient.invalidateQueries({
        queryKey: [
          "folder-entities",
          workspaceId.toString(),
          undefined,
          undefined,
          undefined,
          true,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [
          "folder-entities",
          String(workspaceId),
          selectedFolderId ? Number(selectedFolderId) : undefined,
        ],
      });

      setOpen(false);
    } catch (error: unknown) {
      const itemCount = entities.length;
      const itemText = itemCount === 1 ? entities[0].name || "Item" : `${itemCount} items`;

      if (error instanceof Error && error.message.includes("Failed to move some items:")) {
        displayToast(error.message, ToastType.error);
      } else {
        displayToast(`Failed to move ${itemText}`, ToastType.error);
      }
    }
  };

  return {
    selectedFolderId,
    handleSelectFolder,
    handleGoBack,
    handleMove,
    searchTerm,
    setSearchTerm,
    currentFolder,
    breadcrumbs,
    folderList,
    allFolders,
    setSelectedFolderId,
  };
};
